import { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";

// Use relative paths so the same build works on any domain (preview, staging, production).
// The Kubernetes ingress routes /api/* to the backend regardless of host.
const API = "";

const AdminAuthContext = createContext(null);

export function AdminAuthProvider({ children }) {
  const [authenticated, setAuthenticated] = useState(false);
  const [checking, setChecking] = useState(true);

  const verify = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/admin/auth/verify`, {
        credentials: "include",
      });
      setAuthenticated(res.ok);
    } catch (err) {
      console.error("[AdminAuth] verify failed:", err);
      setAuthenticated(false);
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => {
    verify();
  }, [verify]);

  const login = useCallback(async (password) => {
    const res = await fetch(`${API}/api/admin/auth/login`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ password }),
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      const err = new Error(data.detail || "Login failed");
      err.response = { data };
      throw err;
    }
    setAuthenticated(true);
  }, []);

  const logout = useCallback(async () => {
    try {
      await fetch(`${API}/api/admin/auth/logout`, {
        method: "POST",
        credentials: "include",
      });
    } catch (err) { console.warn("Logout network error (session cleared client-side):", err.message); }
    setAuthenticated(false);
  }, []);

  const value = useMemo(
    () => ({ authenticated, checking, login, logout }),
    [authenticated, checking, login, logout]
  );

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  );
}

export function useAdminAuth() {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) throw new Error("useAdminAuth must be used within AdminAuthProvider");
  return ctx;
}
